import {
  Ref,
  forwardRef,
  useMemo,
  useState,
} from 'react';
import {motion} from 'framer-motion';
import {useDropDown} from '../../../../hooks';
import {Label} from '../../../atoms/label';
import {FieldWrapper} from '../../../atoms/field-wrapper';
import {Helper} from '../../../atoms/helper';
import {
  Wrapper,
  DropdownWrapper,
  DropWrapper,
  DropdownHandler,
  HandlerWrapper,
  ArrowIconWrapper,
  OptionsWrapper,
} from './styled';
import {Body2} from '../../../atoms/typography';
import {ArrowHeadDownIcon} from '../../../atoms/icons/arrow-head-down';
import {SearchBox2} from '../../search-box';
import {VerticalSpacer} from '../../../atoms/spacer';
import {
  OptionsSelectFieldProps,
  SelectOptionItem,
  GroupedSelectOption,
} from './types';

interface SelectOptsProps {
  option: GroupedSelectOption;
  handleChange: (option: SelectOptionItem) => void;
  selectedOption: SelectOptionItem;
  searchValue: string;
}

export const OptionsSelectField = forwardRef(
  (
    {
      label,
      state,
      helper,
      placeholder,
      options = [],
      onChange,
      defaultValue,
      disabled,
    }: OptionsSelectFieldProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    const {handleOpen, ref: dropRef, open, handleClose} = useDropDown();
    const [searchValue, setSearchValue] = useState('');

    const filterOption = useMemo(() => {
      if (!searchValue) return options;
      return options.map((option) => ({
        ...option,
        data: option.data.filter((group) =>
          group.label.toLowerCase().includes(searchValue.toLowerCase()),
        ),
      }));
    }, [options, searchValue]);

    const [selectedOption, setSelectedOption] = useState<SelectOptionItem>(
      defaultValue || {value: '', label: ''},
    );

    const handleChange = (option: SelectOptionItem) => {
      setSelectedOption(option);
      handleClose();
      onChange?.(option);
    };

    const displayLabel = selectedOption?.label;

    return (
      <FieldWrapper margin={true} ref={ref}>
        <Wrapper>
          <Label aria-label="label">{label}</Label>
          <DropWrapper ref={dropRef}>
            <DropdownHandler
              open={open}
              performance={true}
              onClick={handleOpen}
              disabled={disabled}>
              <HandlerWrapper>
                <OptionsWrapper disabled={disabled}>
                  {displayLabel?.length > 0 && !disabled ? (
                    <Body2>{displayLabel}</Body2>
                  ) : (
                    <p className="greyOut">{placeholder}</p>
                  )}
                </OptionsWrapper>
                <ArrowIconWrapper disabled={disabled}>
                  <motion.span
                    initial={{rotate: 0}}
                    animate={{
                      rotate: !disabled && open ? 180 : 0,
                      marginTop: !disabled && open ? '-6px' : '0px',
                    }}
                    transition={{
                      duration: 0.3,
                    }}
                    key="options-select-toggled">
                    <ArrowHeadDownIcon />
                  </motion.span>
                </ArrowIconWrapper>
              </HandlerWrapper>
            </DropdownHandler>
            {open && (
              <DropdownWrapper
                style={{
                  padding: '8px',
                  maxHeight: '300px',
                  overflow: 'scroll',
                }}>
                <SearchBox2
                  placeholder={'Search report'}
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  handleCancel={() => setSearchValue('')}
                  style={{height: '44px'}}
                />
                {filterOption.length > 0 &&
                filterOption.some((opt) => opt?.data?.length > 0) ? (
                  filterOption.map((option) => (
                    <SelectOpts
                      option={option}
                      searchValue={searchValue}
                      handleChange={handleChange}
                      selectedOption={selectedOption}
                      key={option.name}
                    />
                  ))
                ) : (
                  <div>
                    <Body2
                      align="center"
                      kind="textBody"
                      style={{margin: '10px 0px'}}>
                      Report not found
                    </Body2>
                  </div>
                )}
              </DropdownWrapper>
            )}
          </DropWrapper>
          <Helper aria-label="helper" state={state} children={helper} />
        </Wrapper>
      </FieldWrapper>
    );
  },
);

const SelectOpts = ({
  option,
  handleChange,
  selectedOption,
  searchValue,
}: SelectOptsProps) => {
  const [open, setOpen] = useState(false);
  useMemo(() => {
    if (searchValue !== '' && !open) {
      setOpen(true);
    }
  }, [searchValue, open]);
  return (
    <>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '14px',
          padding: '5px 8px',
        }}>
        <Body2
          style={{fontWeight: open ? 500 : 400}}
          kind={open ? 'textDark' : 'textBody'}>
          {option.name}
        </Body2>
        <ArrowIconWrapper>
          <motion.span
            initial={{rotate: 0}}
            animate={{
              rotate: open ? 180 : 0,
              marginTop: open ? '-6px' : '0px',
            }}
            transition={{
              duration: 0.3,
            }}
            key="options-select-items-toggled">
            <ArrowHeadDownIcon />
          </motion.span>
        </ArrowIconWrapper>
      </div>
      {open && (
        <>
          <div
            style={{
              background: '#fafafa',
              borderTop: '1px solid #ededf2',
              borderRadius: '0px 0px 10px 10px',
            }}>
            {option?.data?.map((opt) => (
              <div
                key={opt.value}
                onClick={() => {
                  handleChange(opt);
                }}
                style={{
                  display: 'flex',
                  padding: '16px 0px',
                  cursor: 'pointer',
                }}>
                <Body2
                  kind={
                    selectedOption?.label === opt.label
                      ? 'textDark'
                      : 'textBody'
                  }
                  style={{marginLeft: '5px'}}>
                  {opt.label}
                </Body2>
              </div>
            ))}
          </div>
          <VerticalSpacer size="24px" />
        </>
      )}
    </>
  );
};
